import { Download, Share2, X } from "lucide-react";
import { useEffect, useState } from "react";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type PromptMode = "native" | "ios";

const DISMISSED_AT_KEY = "ja:pwa-install-dismissed-at";
const DISMISS_FOR_MS = 3 * 24 * 60 * 60 * 1000;

const isInstalled = () => window.matchMedia("(display-mode: standalone), (display-mode: fullscreen)").matches
  || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);

const isIosDevice = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
  || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export function PwaInstallPrompt({ authSurface = false }: { authSurface?: boolean }) {
  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);
  const [mode, setMode] = useState<PromptMode | null>(null);

  useEffect(() => {
    if (isInstalled()) return;
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_AT_KEY) ?? 0);
    if (Date.now() - dismissedAt < DISMISS_FOR_MS) return;

    const ios = isIosDevice();
    const handleInstallAvailable = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as InstallPromptEvent);
      setMode("native");
    };
    const handleInstalled = () => {
      setMode(null);
      setInstallEvent(null);
      window.localStorage.removeItem(DISMISSED_AT_KEY);
    };

    window.addEventListener("beforeinstallprompt", handleInstallAvailable);
    window.addEventListener("appinstalled", handleInstalled);

    if (ios) setMode("ios");

    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallAvailable);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  if (!mode) return null;

  const dismiss = () => {
    window.localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    setMode(null);
  };

  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === "accepted") setMode(null);
    else dismiss();
    setInstallEvent(null);
  };

  return <aside className={`pwa-install-prompt is-${mode}${authSurface ? " is-auth-surface" : ""}`} role="dialog" aria-label="Instalar aplicativo JA" aria-live="polite">
    <div className="pwa-install-icon" aria-hidden="true">
      {mode === "native" ? <Download size={22} /> : <Share2 size={22} />}
    </div>
    <div className="pwa-install-copy">
      <strong>{mode === "native" ? "Instalar app JA" : "Compartilhar › Tela de Início"}</strong>
      {mode === "native"
        ? <span>Use como aplicativo e acesse mais rápido.</span>
        : <span>Toque em Compartilhar e depois em “Adicionar à Tela de Início”.</span>}
    </div>
    {mode === "native" && <button type="button" className="site-button site-button-red site-button-small pwa-install-action" onClick={() => void install()}>Instalar</button>}
    <button type="button" className="pwa-install-close" onClick={dismiss} aria-label="Lembrar depois"><X size={18} /></button>
  </aside>;
}
